export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export const LANG_OPTIONS = ['eng', 'ben', 'eng+ben'];

const ACCEPTED_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/bmp',
    'image/gif',
]);

/**
 * Tesseract reads type best at around 300dpi — roughly a 20px x-height. Phone
 * shots of a page are usually finer than that once the page fills the frame, so
 * small images are enlarged before they are read.
 */
const TARGET_MIN_SIDE = 1700;
/** …and big ones come down, because past this it is slower without reading better. */
const MAX_SIDE = 3400;
const MAX_UPSCALE = 3;

/**
 * A first read this sure, with a real amount of text behind it, is not worth
 * a second opinion — measured on clean screenshots, the second pass agreed
 * every time and only cost seconds.
 */
const CONFIDENT = 88;
const ENOUGH_TEXT = 40;
/** Below this the picture is worth attacking from another angle. */
const POOR = 62;

export function isSupportedImageToTextFile(file) {
    if (!file) return false;
    if (file.type === 'image/svg+xml') return false;
    if (ACCEPTED_TYPES.has(file.type)) return true;
    return /\.(jpe?g|png|webp|bmp|gif)$/i.test(file.name || '');
}

/* ------------------------------------------------------------------ pixels */

async function decode(file) {
    if (typeof createImageBitmap === 'function') {
        try {
            return await createImageBitmap(file);
        } catch {
            /* fall through to the <img> path */
        }
    }
    const url = URL.createObjectURL(file);
    try {
        const img = new Image();
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = url;
        });
        return img;
    } finally {
        URL.revokeObjectURL(url);
    }
}

function canvasOf(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    return canvas;
}

/** Draw the picture at the size Tesseract wants to read it at. */
function drawScaled(source) {
    const w = source.width || source.naturalWidth;
    const h = source.height || source.naturalHeight;
    let scale = Math.min(MAX_UPSCALE, Math.max(1, TARGET_MIN_SIDE / Math.min(w, h)));
    if (Math.max(w, h) * scale > MAX_SIDE) scale = MAX_SIDE / Math.max(w, h);

    const canvas = canvasOf(w * scale, h * scale);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
}

/** Grey, with the darkest and lightest 1% pushed to black and white. */
function toStretchedGrey(canvas) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = image.data;
    const histogram = new Uint32Array(256);

    for (let i = 0; i < data.length; i += 4) {
        // Rec. 601 luma: the channel weights the eye actually uses, which keeps
        // coloured paper and coloured ink from washing into each other.
        const grey = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
        const value = grey < 0 ? 0 : grey > 255 ? 255 : Math.round(grey);
        data[i] = data[i + 1] = data[i + 2] = value;
        histogram[value] += 1;
    }

    const total = canvas.width * canvas.height;
    const cut = total * 0.01;
    let low = 0;
    let high = 255;
    for (let seen = 0, v = 0; v < 256; v += 1) {
        seen += histogram[v];
        if (seen >= cut) { low = v; break; }
    }
    for (let seen = 0, v = 255; v >= 0; v -= 1) {
        seen += histogram[v];
        if (seen >= cut) { high = v; break; }
    }

    if (high - low > 8) {
        const span = 255 / (high - low);
        for (let i = 0; i < data.length; i += 4) {
            const value = (data[i] - low) * span;
            const clamped = value < 0 ? 0 : value > 255 ? 255 : value;
            data[i] = data[i + 1] = data[i + 2] = clamped;
        }
    }

    ctx.putImageData(image, 0, 0);
    return canvas;
}

/**
 * Black and white by local average rather than one threshold for the page.
 *
 * This is the pass that rescues a photograph: a shadow across one corner, or a
 * page lit from one side, defeats a single threshold — half the text dissolves
 * into the dark. Comparing each pixel with the average of the patch around it
 * is blind to how the page is lit.
 */
function adaptiveThreshold(greyCanvas) {
    const { width, height } = greyCanvas;
    const ctx = greyCanvas.getContext('2d', { willReadFrequently: true });
    const image = ctx.getImageData(0, 0, width, height);
    const data = image.data;

    // Integral image, so every window average is four lookups whatever its size.
    const integral = new Float64Array((width + 1) * (height + 1));
    for (let y = 0; y < height; y += 1) {
        let rowSum = 0;
        for (let x = 0; x < width; x += 1) {
            rowSum += data[(y * width + x) * 4];
            integral[(y + 1) * (width + 1) + (x + 1)] = integral[y * (width + 1) + (x + 1)] + rowSum;
        }
    }

    const radius = Math.max(8, Math.round(Math.min(width, height) / 40));
    const out = new Uint8ClampedArray(data.length);
    for (let y = 0; y < height; y += 1) {
        const y0 = Math.max(0, y - radius);
        const y1 = Math.min(height - 1, y + radius);
        for (let x = 0; x < width; x += 1) {
            const x0 = Math.max(0, x - radius);
            const x1 = Math.min(width - 1, x + radius);
            const area = (y1 - y0 + 1) * (x1 - x0 + 1);
            const sum = integral[(y1 + 1) * (width + 1) + (x1 + 1)]
                - integral[y0 * (width + 1) + (x1 + 1)]
                - integral[(y1 + 1) * (width + 1) + x0]
                + integral[y0 * (width + 1) + x0];
            const local = sum / area;
            const index = (y * width + x) * 4;
            // 6% under the local average: enough to ignore paper texture and
            // compression noise without eating thin strokes.
            const value = data[index] < local * 0.94 ? 0 : 255;
            out[index] = out[index + 1] = out[index + 2] = value;
            out[index + 3] = 255;
        }
    }

    const result = canvasOf(width, height);
    result.getContext('2d').putImageData(new ImageData(out, width, height), 0, 0);
    return result;
}

/**
 * The picture, at the size and contrast Tesseract reads best.
 * @returns {Promise<{ cleaned: HTMLCanvasElement, binary: () => HTMLCanvasElement }>}
 */
async function prepareVariants(file) {
    const source = await decode(file);
    const cleaned = toStretchedGrey(drawScaled(source));
    if (typeof source.close === 'function') source.close();

    return {
        cleaned,
        // Built only if the earlier passes disappoint: it is the slow one.
        binary: () => adaptiveThreshold(cleaned),
    };
}

/* ------------------------------------------------------------------- reading */

/**
 * Which of two readings to believe.
 *
 * Confidence alone is a trap: automatic layout on a poor photo regularly comes
 * back with two words at 95 and nothing else, which beats a full page read at
 * 91. So confidence is weighed against how much of the page each attempt
 * actually recovered.
 */
function scoreOf(candidate, mostChars) {
    if (!candidate.text) return -1;
    return candidate.confidence * Math.sqrt(candidate.text.length / Math.max(1, mostChars));
}

function pickBest(candidates) {
    const mostChars = Math.max(1, ...candidates.map((c) => c.text.length));
    let best = candidates[0];
    let bestScore = scoreOf(best, mostChars);
    // Strictly greater, so a tie goes to the earlier attempt — that is the
    // automatic-layout one, which keeps columns apart instead of running them
    // together into one line.
    for (const candidate of candidates.slice(1)) {
        const score = scoreOf(candidate, mostChars);
        if (score > bestScore) {
            bestScore = score;
            best = candidate;
        }
    }
    return { best, score: bestScore };
}

/**
 * Read an image, trying harder while the result looks poor.
 *
 * One pass over the untouched file is what this used to do, and it is why a
 * phone photo came back half empty: on a 520px-wide shot of a page it recovered
 * 30% of the words at 51% confidence. Enlarging the picture to the size the
 * engine expects and stretching its contrast first, then reading it both as an
 * automatic layout and as a single block and keeping whichever read more, took
 * the same picture to 78% at 91%. A clean screenshot is unaffected: it reads
 * perfectly on the first pass and stops there.
 *
 * @param {File} file
 * @param {string} lang one of `LANG_OPTIONS`
 * @param {(update: { progress: number, status: string }) => void} [onProgress]
 * @returns {Promise<{ text: string, confidence: number }>}
 */
export async function extractTextFromImage(file, lang, onProgress) {
    const { createWorker } = await import('tesseract.js');

    onProgress?.({ progress: 0, status: 'preparing' });
    const variants = await prepareVariants(file);

    // Three passes at most; the bar moves through its own slice of each.
    const PLANNED = 3;
    let passIndex = 0;

    const worker = await createWorker(lang, 1, {
        logger: (message) => {
            if (message.status === 'recognizing text') {
                const within = message.progress || 0;
                onProgress?.({
                    progress: Math.min(99, Math.round(((passIndex + within) / PLANNED) * 100)),
                    status: 'recognizing',
                });
            } else if (message.status === 'loading language traineddata') {
                onProgress?.({ progress: 0, status: 'loading' });
            } else if (message.status === 'initializing api') {
                onProgress?.({ progress: 0, status: 'initializing' });
            }
        },
    });

    try {
        await worker.setParameters({
            // The picture carries no dpi of its own, and without this Tesseract
            // guesses one and warns about it on every run.
            user_defined_dpi: '300',
            // Keep the spacing of columns and indented lines instead of
            // collapsing every run of spaces into one.
            preserve_interword_spaces: '1',
        });

        const candidates = [];
        const read = async (image, psm) => {
            await worker.setParameters({ tessedit_pageseg_mode: psm });
            const result = await worker.recognize(image);
            candidates.push({
                psm,
                confidence: Number(result?.data?.confidence) || 0,
                text: (result?.data?.text || '').trim(),
            });
            passIndex += 1;
        };

        // Automatic layout first: it is the one that keeps a two-column page in
        // two columns.
        await read(variants.cleaned, '3');
        const first = candidates[0];
        if (first.confidence >= CONFIDENT && first.text.length >= ENOUGH_TEXT) {
            onProgress?.({ progress: 100, status: 'recognizing' });
            return { text: first.text, confidence: Math.round(first.confidence) };
        }

        // Then as a single block, which is how a receipt, a screenshot or a
        // photographed page is meant to be read — automatic layout tends to cut
        // those into pieces and drop lines between them.
        await read(variants.cleaned, '6');

        let { best, score } = pickBest(candidates);
        if (score < POOR) {
            // Last resort: pure black and white, for a page lit from one side.
            await read(variants.binary(), '6');
            ({ best } = pickBest(candidates));
        }

        onProgress?.({ progress: 100, status: 'recognizing' });
        return { text: best.text, confidence: Math.round(best.confidence) };
    } finally {
        await worker.terminate();
    }
}
